import { useEffect, useState } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Play, Square, Timer, Clock, Pause, Coffee } from "lucide-react";
import { formatSeconds, formatDuration } from "./helpers";

export const TimerCard = ({ order, onOrderUpdate, onOpenManualTime, totals }) => {
  const { user } = useAuth();
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const active = order?.time_entries?.find(e => e.is_active);
    setActiveTimer(active || null);
  }, [order]);

  useEffect(() => {
    if (!activeTimer) { setElapsedTime(0); return; }
    const updateElapsed = () => {
      const dateParts = activeTimer.date?.split('.') || [];
      const timeParts = activeTimer.start_time?.split(':') || [];
      let start;
      if (dateParts.length === 3 && timeParts.length === 2) {
        start = new Date(
          parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]),
          parseInt(timeParts[0]), parseInt(timeParts[1]), 0
        );
      } else {
        start = new Date(activeTimer.start_time);
      }
      const diff = Math.floor((new Date() - start) / 1000);
      setElapsedTime(diff >= 0 ? diff : 0);
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const startTimer = async () => {
    try {
      const res = await api.post(`/api/work-orders/${order.id}/time/start`);
      onOrderUpdate?.(res.data);
      toast.success("Zeiterfassung gestartet");
    } catch { toast.error("Fehler beim Starten"); }
  };
  const stopTimer = async () => {
    try {
      const res = await api.post(`/api/work-orders/${order.id}/time/stop`);
      onOrderUpdate?.(res.data);
      toast.success("Zeiterfassung gestoppt");
    } catch { toast.error("Fehler beim Stoppen"); }
  };
  const togglePause = async () => {
    try {
      const res = await api.post(`/api/work-orders/${order.id}/time/pause`);
      const newActive = res.data.time_entries?.find(e => e.is_active);
      onOrderUpdate?.(res.data);
      toast.success(newActive?.pause_start ? "Pause gestartet" : "Arbeit fortgesetzt");
    } catch { toast.error("Fehler bei Pause"); }
  };

  const inactiveEntries = (order?.time_entries || []).filter(e => !e.is_active);

  return (
    <Card className="border-2 border-primary/20" data-testid="detail-timer-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Timer className="w-5 h-5 text-primary" />Zeiterfassung
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          {!activeTimer ? (
            <Button onClick={startTimer} className="gap-2" data-testid="detail-start-timer-btn">
              <Play className="w-4 h-4" />Starten
            </Button>
          ) : (
            <>
              <div className={`text-2xl font-mono font-bold ${activeTimer?.pause_start ? 'text-orange-500' : 'text-primary'}`}>
                {formatSeconds(elapsedTime)}
                {activeTimer?.pause_start && <span className="text-sm ml-2 font-normal">(Pause)</span>}
              </div>
              <div className="flex gap-2">
                <Button onClick={togglePause} variant={activeTimer?.pause_start ? "default" : "outline"} className="gap-2">
                  {activeTimer?.pause_start ? (<><Play className="w-4 h-4" />Fortsetzen</>) : (<><Coffee className="w-4 h-4" />Pause</>)}
                </Button>
                <Button onClick={stopTimer} variant="destructive" className="gap-2" data-testid="detail-stop-timer-btn">
                  <Square className="w-4 h-4" />Stoppen
                </Button>
              </div>
            </>
          )}
          {user?.role === "admin" && (
            <Button variant="outline" onClick={onOpenManualTime} className="gap-2" data-testid="detail-manual-time-btn">
              <Clock className="w-4 h-4" />Nachtrag
            </Button>
          )}
        </div>

        {activeTimer && (
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Eingestempelt:</span>
              <span className="font-medium">{activeTimer.start_time} Uhr</span>
              <span className="text-muted-foreground">({activeTimer.date})</span>
            </div>
            {activeTimer.pause_minutes > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Coffee className="w-4 h-4 text-orange-500" />
                <span className="text-muted-foreground">Bisherige Pause:</span>
                <span className="font-medium text-orange-600">{activeTimer.pause_minutes} min</span>
              </div>
            )}
            {activeTimer.pause_start && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <Pause className="w-4 h-4" />
                <span>Pause läuft seit {new Date(activeTimer.pause_start).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="text-sm">
            <span className="text-muted-foreground">Erfasste Zeit:</span>
            <span className="ml-2 font-medium">{formatDuration(totals.recordedMinutes)}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Geplante Zeit:</span>
            <span className="ml-2 font-medium">{formatDuration(totals.totalTime)}</span>
          </div>
        </div>

        {inactiveEntries.length > 0 && (
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="history" className="border-none">
              <AccordionTrigger className="py-2 text-sm">
                Zeiteinträge anzeigen ({inactiveEntries.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {inactiveEntries.map((entry, idx) => {
                    const sParts = entry.start_time?.split(':') || [];
                    const eParts = entry.end_time?.split(':') || [];
                    let workMinutes = 0;
                    if (sParts.length === 2 && eParts.length === 2) {
                      const sM = parseInt(sParts[0]) * 60 + parseInt(sParts[1]);
                      const eM = parseInt(eParts[0]) * 60 + parseInt(eParts[1]);
                      workMinutes = eM - sM - (entry.pause_minutes || 0);
                    }
                    return (
                      <div key={idx} className="bg-muted/50 p-3 rounded-lg space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">{entry.date}</span>
                          <span className="text-sm font-bold text-primary">
                            {workMinutes > 0 ? formatDuration(workMinutes) : '-'}
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
                          <span>Start: {entry.start_time}</span>
                          <span>Ende: {entry.end_time}</span>
                          {entry.pause_minutes > 0 && <span className="text-orange-600">Pause: {entry.pause_minutes} min</span>}
                          {entry.is_manual && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Nachtrag</span>}
                          {entry.notes && <span className="italic">{entry.notes}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};
